import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Users, UserCheck, UserPlus } from 'lucide-react';
import { ClientSummaryTable } from './ClientSummaryTable';
import { ClientSummaryRow } from './ClientSummaryRow';
import { Modal } from '../admin/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { hasRole } from '../../utils/userRoleHelpers';
import toast from 'react-hot-toast';

type TabType = 'my-clients' | 'all-clients';

interface TabConfig {
  id: TabType;
  label: string;
  icon: React.ReactNode;
  count?: number;
}

interface StatusCount {
  status: string;
  label: string;
  icon: string;
  count: number;
}

export const CoachDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  // Initialize activeTab from URL query parameter, default to 'my-clients'
  const tabFromUrl = searchParams.get('tab') as TabType;
  const initialTab: TabType = (tabFromUrl === 'my-clients' || tabFromUrl === 'all-clients') ? tabFromUrl : 'my-clients';
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [myClientsCount, setMyClientsCount] = useState<number>(0);
  const [allClientsCount, setAllClientsCount] = useState<number>(0);
  const [myClientsStatusCounts, setMyClientsStatusCounts] = useState<StatusCount[]>([]);
  const [allClientsStatusCounts, setAllClientsStatusCounts] = useState<StatusCount[]>([]);

  // Invite client modal
  const canInvite = hasRole(user, ['admin', 'invite']);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '', phone: '' });
  const [inviteLoading, setInviteLoading] = useState(false);

  const handleInviteClient = async () => {
    if (!inviteForm.email || !inviteForm.firstName || !inviteForm.lastName) {
      toast.error('Email, first name, and last name are required');
      return;
    }
    setInviteLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/admin/clients/invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          email: inviteForm.email,
          firstName: inviteForm.firstName,
          lastName: inviteForm.lastName,
          phone: inviteForm.phone || undefined,
          role: 'client'
        })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(`Invitation sent to ${inviteForm.email}`);
        setShowInviteModal(false);
        setInviteForm({ email: '', firstName: '', lastName: '', phone: '' });
      } else {
        toast.error(data.error || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  const tabs: TabConfig[] = [
    {
      id: 'my-clients',
      label: 'My Clients',
      icon: <UserCheck className="w-4 h-4" />,
      count: myClientsCount
    },
    {
      id: 'all-clients',
      label: 'All Clients',
      icon: <Users className="w-4 h-4" />,
      count: allClientsCount
    }
  ];

  const handleMyClientsCountUpdate = useCallback((count: number) => {
    setMyClientsCount(count);
  }, []);

  const handleAllClientsCountUpdate = useCallback((count: number) => {
    setAllClientsCount(count);
  }, []);

  const handleMyClientsStatusCountsUpdate = useCallback((counts: StatusCount[]) => {
    setMyClientsStatusCounts(counts);
  }, []);

  const handleAllClientsStatusCountsUpdate = useCallback((counts: StatusCount[]) => {
    setAllClientsStatusCounts(counts);
  }, []);

  // Handle tab change - update both state and URL
  const handleTabChange = useCallback((tabId: TabType) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  }, [setSearchParams]);

  // Sync activeTab state with URL changes (browser back/forward)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab') as TabType;
    const validTab: TabType = (tabFromUrl === 'my-clients' || tabFromUrl === 'all-clients') ? tabFromUrl : 'my-clients';
    setActiveTab(validTab);
  }, [searchParams]);

  // Eagerly load both tab counts on mount
  useEffect(() => {
    const loadTabCounts = async () => {
      const token = localStorage.getItem('auth_token');
      const headers = {
        'Authorization': `Bearer ${token}`
      };

      try {
        // Load My Clients count
        const myClientsResponse = await fetch('/api/coach/dashboard/summary', { headers });
        if (myClientsResponse.ok) {
          const myClientsData = await myClientsResponse.json();
          if (myClientsData.clients) {
            setMyClientsCount(myClientsData.clients.length);
          }
        }

        // Load All Clients count
        const allClientsResponse = await fetch('/api/coach/dashboard/all', { headers });
        if (allClientsResponse.ok) {
          const allClientsData = await allClientsResponse.json();
          if (allClientsData.clients) {
            setAllClientsCount(allClientsData.clients.length);
          }
        }
      } catch (error) {
        console.error('Error loading tab counts:', error);
      }
    };

    loadTabCounts();
  }, []);

  // Get current tab's data for summary row
  const currentCount = activeTab === 'my-clients' ? myClientsCount : allClientsCount;
  const currentStatusCounts = activeTab === 'my-clients' ? myClientsStatusCounts : allClientsStatusCounts;

  return (
    <div className="coach-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-row">
          <div>
            <h1>Client Management</h1>
            <p className="subtitle">View and manage your client roster</p>
          </div>
          {canInvite && (
            <button className="invite-btn" onClick={() => setShowInviteModal(true)}>
              <UserPlus className="w-4 h-4" />
              Invite Client
            </button>
          )}
        </div>
      </div>

      {/* Phase 4: Summary Row */}
      {currentCount > 0 && (
        <ClientSummaryRow
          totalClients={currentCount}
          statusCounts={currentStatusCounts}
        />
      )}

      {/* Tabs */}
      <div className="tabs-container">
        <div className="tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`tab ${activeTab === tab.id ? 'active' : ''}`}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
              {tab.count !== undefined && (
                <span className="tab-count">{tab.count}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'my-clients' && (
          <ClientSummaryTable
            scope="my-clients"
            onCountUpdate={handleMyClientsCountUpdate}
            onStatusCountsUpdate={handleMyClientsStatusCountsUpdate}
          />
        )}
        {activeTab === 'all-clients' && (
          <ClientSummaryTable
            scope="all-clients"
            onCountUpdate={handleAllClientsCountUpdate}
            onStatusCountsUpdate={handleAllClientsStatusCountsUpdate}
          />
        )}
      </div>

      {/* Invite Client Modal */}
      {showInviteModal && (
        <Modal
          isOpen={showInviteModal}
          onClose={() => { setShowInviteModal(false); setInviteForm({ email: '', firstName: '', lastName: '', phone: '' }); }}
          title="Invite New Client"
          size="small"
          footer={
            <>
              <button className="btn btn-secondary" onClick={() => setShowInviteModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleInviteClient} disabled={inviteLoading}>
                {inviteLoading ? 'Sending...' : 'Send Invitation'}
              </button>
            </>
          }
        >
          <div className="invite-form">
            <div className="form-group">
              <label htmlFor="invite-email">Email *</label>
              <input id="invite-email" type="email" value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="client@example.com" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="invite-first">First Name *</label>
                <input id="invite-first" type="text" value={inviteForm.firstName}
                  onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                  placeholder="First name" />
              </div>
              <div className="form-group">
                <label htmlFor="invite-last">Last Name *</label>
                <input id="invite-last" type="text" value={inviteForm.lastName}
                  onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                  placeholder="Last name" />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="invite-phone">Phone</label>
              <input id="invite-phone" type="tel" value={inviteForm.phone}
                onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                placeholder="(555) 123-4567" />
            </div>
          </div>
        </Modal>
      )}

      <style>{`
        .coach-dashboard {
          padding: 24px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .dashboard-header {
          margin-bottom: 24px;
        }

        .header-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }

        .dashboard-header h1 {
          color: var(--text-primary);
          font-size: 28px;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .dashboard-header .subtitle {
          color: var(--text-secondary);
          font-size: 14px;
          margin: 0;
        }

        .invite-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: var(--accent-green);
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .invite-btn:hover {
          background: var(--accent-green-hover);
        }

        .invite-form .form-group {
          margin-bottom: 16px;
        }

        .invite-form .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }

        .invite-form label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }

        .invite-form input {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid var(--border);
          border-radius: 6px;
          font-size: 14px;
          background: var(--bg-secondary);
          color: var(--text-primary);
          box-sizing: border-box;
        }

        .invite-form input:focus {
          outline: none;
          border-color: var(--accent-primary, #3b82f6);
        }

        .tabs-container {
          border-bottom: 1px solid var(--border);
          margin-bottom: 24px;
        }

        .tabs {
          display: flex;
          gap: 4px;
        }

        .tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--text-secondary);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          bottom: -1px;
        }

        .tab:hover {
          color: var(--text-primary);
          background: var(--hover);
        }

        .tab.active {
          color: var(--primary);
          border-bottom-color: var(--primary);
        }

        .tab-icon {
          display: flex;
          align-items: center;
        }

        .tab-count {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          background: var(--primary-light);
          color: var(--primary);
          border-radius: 10px;
          font-size: 12px;
          font-weight: 600;
        }

        .tab.active .tab-count {
          background: var(--primary);
          color: white;
        }

        .tab-content {
          min-height: 400px;
        }

        .placeholder-content {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 80px 20px;
          text-align: center;
        }

        .placeholder-icon {
          color: var(--text-muted);
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .placeholder-content h2 {
          color: var(--text-primary);
          font-size: 20px;
          font-weight: 600;
          margin: 0 0 8px 0;
        }

        .placeholder-content p {
          color: var(--text-secondary);
          font-size: 14px;
          margin: 4px 0;
        }

        .placeholder-subtitle {
          max-width: 500px;
          color: var(--text-muted) !important;
          font-size: 13px !important;
          margin-top: 12px !important;
        }
      `}</style>
    </div>
  );
};
